/* ============================================================
   LH ACCESSORY-ORPHAN RECONCILE
   ------------------------------------------------------------
   Fixes the Globo accessory-orphan bug: when a Globo accessory
   CHILD line item is removed, the parent line keeps the stamped
   value (a comma-joined list for multi-select), stranding it in
   the order and on the /cart page.

   Covers BOTH removal surfaces through the one event they each
   already dispatch: document 'theme:cart:change'.
     - Cart drawer: assets/lh-cart-drawer.js changeLine()
     - /cart page:  vendor theme.js updateCart() (untouched)

   Behaviour: removal-triggered (diff of prev vs new cart), value
   level (rebuild from surviving children), linkage-based (never a
   hardcoded field name), idempotent, and a strict no-op unless a
   Globo child line was actually removed. No MutationObserver.
============================================================ */
(function () {
  'use strict';
  if (window.__lhAccessoryReconcile) return;
  window.__lhAccessoryReconcile = true;

  var prevCart = null;
  var busy = false;

  /* properties -> array of [key, value], tolerant of object or array shape */
  function pairs(line) {
    var p = line && line.properties;
    if (!p) return [];
    if (Array.isArray(p)) return p.slice();
    return Object.keys(p).map(function (k) { return [k, p[k]]; });
  }
  function readProp(line, key) {
    var e = pairs(line);
    for (var i = 0; i < e.length; i++) { if (e[i][0] === key) return e[i][1]; }
    return null;
  }
  /* A Globo child carries BOTH of these. Ordinary products, parent
     lines, and bundle-widget lines do not, so they never trigger. */
  function childLinkage(line) {
    var g = readProp(line, '_gpo_parent_product_group');
    var f = readProp(line, '_gpo_field_name');
    return (g != null && f != null) ? { group: String(g), field: String(f) } : null;
  }
  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  /* Rebuild parent property `field` from the surviving children of `group`.
     Sends the FULL property set with only `field` changed, so every other
     property (colour choice, topcoat finish, _gpo_product_group, _has_gpo,
     etc.) is carried through byte-for-byte. Returns the updated cart, or
     null on no-op / no parent. */
  function reconcile(cart, group, field) {
    var parent = null;
    var survivors = [];
    (cart.items || []).forEach(function (l) {
      if (!parent && readProp(l, '_gpo_product_group') === group) parent = l;
      var lk = childLinkage(l);
      if (lk && lk.group === group && lk.field === field) survivors.push(l);
    });
    if (!parent) return Promise.resolve(null);

    var newVal = survivors
      .map(function (l) { return l.variant_title; })
      .filter(function (v) { return v != null && v !== ''; })
      .join(', ');

    var current = readProp(parent, field);
    var curStr = (current == null) ? null : String(current);

    /* No-op guards */
    if (survivors.length === 0) {
      if (curStr == null) return Promise.resolve(null);   /* already absent */
    } else if (curStr === newVal) {
      return Promise.resolve(null);                       /* already correct */
    }

    /* Full property set, one key changed */
    var out = {};
    pairs(parent).forEach(function (pr) { out[pr[0]] = pr[1]; });
    if (survivors.length === 0) { delete out[field]; } else { out[field] = newVal; }

    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: parent.key, properties: out })
    }).then(function (r) { return r.ok ? r.json() : null; });
  }

  /* On each cart change, find Globo CHILD lines that were present before
     and are gone now, and reconcile their (group, field). */
  function onCartChange(newCart) {
    if (!newCart || !newCart.items) return;
    if (busy) { prevCart = newCart; return; }
    if (!prevCart) { prevCart = newCart; return; }

    var nowKeys = {};
    newCart.items.forEach(function (l) { nowKeys[l.key] = true; });

    var todo = {};
    prevCart.items.forEach(function (l) {
      if (nowKeys[l.key]) return;                 /* still present: not removed */
      var lk = childLinkage(l);                    /* removed: was it a child?   */
      if (lk) todo[lk.group + '\u0000' + lk.field] = lk;
    });
    var list = Object.keys(todo).map(function (k) { return todo[k]; });
    if (!list.length) { prevCart = newCart; return; }

    busy = true;
    var changed = false;
    var chain = Promise.resolve(newCart);
    list.forEach(function (lk) {
      chain = chain.then(function (cart) {
        return reconcile(cart, lk.group, lk.field).then(function (updated) {
          if (updated) { changed = true; return updated; }
          return cart;
        });
      });
    });
    chain.then(function (finalCart) {
      prevCart = finalCart || newCart;
      busy = false;
      if (changed) afterReconcile();
    }).catch(function () { busy = false; prevCart = newCart; });
  }

  function onCartPage() {
    return document.body && document.body.classList.contains('template-cart');
  }
  function afterReconcile() {
    /* Drawer hides the stamp, so it needs no re-render. The /cart page
       shows it, and its DOM was already rendered with the stale value,
       so reload it to show the corrected cart. This only runs on the
       rare occasion a real orphan was just corrected. */
    if (onCartPage()) { try { window.location.reload(); } catch (e) {} }
  }

  document.addEventListener('theme:cart:change', function (e) {
    var c = e && e.detail && e.detail.cart;
    if (c) { onCartChange(c); }
    else { fetchCart().then(onCartChange).catch(function () {}); }
  }, false);

  /* Prime the baseline so the first removal has something to diff against. */
  function init() { fetchCart().then(function (c) { if (!prevCart) prevCart = c; }).catch(function () {}); }
  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', init); }
  else { init(); }
})();